/**
 * GymRequestStatusBadge Component
 *
 * Visual indicator for gym request status.
 * Uses color-coded badges to clearly communicate request state.
 */

import { type CSSProperties } from 'react';
import type { GymRequestStatus } from '@flash/shared';

/**
 * Props for GymRequestStatusBadge component
 */
export interface GymRequestStatusBadgeProps {
  /** The status to display */
  status: GymRequestStatus;
  /** Optional additional CSS styles */
  style?: CSSProperties;
}

/**
 * Status-specific styles mapping
 */
const statusStyles: Record<GymRequestStatus, { backgroundColor: string; color: string }> = {
  pending: { backgroundColor: '#fef3c7', color: '#92400e' },
  approved: { backgroundColor: '#d1fae5', color: '#065f46' },
  rejected: { backgroundColor: '#fee2e2', color: '#991b1b' },
  changes_requested: { backgroundColor: '#ffedd5', color: '#9a3412' },
};

/**
 * Human-readable status labels
 */
const statusLabels: Record<GymRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  changes_requested: 'Changes Requested',
};

/**
 * GymRequestStatusBadge displays a color-coded badge for request status
 *
 * @example
 * ```tsx
 * <GymRequestStatusBadge status="pending" />
 * <GymRequestStatusBadge status="approved" />
 * <GymRequestStatusBadge status="changes_requested" />
 * ```
 */
export const GymRequestStatusBadge = ({ status, style }: GymRequestStatusBadgeProps) => {
  const statusStyle = statusStyles[status];
  const label = statusLabels[status];

  return (
    <span
      style={{
        ...badgeBaseStyle,
        backgroundColor: statusStyle.backgroundColor,
        color: statusStyle.color,
        ...style,
      }}
    >
      {label}
    </span>
  );
};

/**
 * Base badge styles
 */
const badgeBaseStyle: CSSProperties = {
  display: 'inline-block',
  padding: '4px 12px',
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: '9999px',
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
};

export default GymRequestStatusBadge;
